import { Component, ElementRef, HostListener, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';

import { SwDrawerComponent } from 'src/app/shared/components/sw-drawer/sw-drawer.component';
import { UserManualService } from 'src/app/services/user-manual.service';
import { MANUAL_GROUPS } from './user-manual.content';
import { ManualGroup, ManualStep, ManualTopic } from './user-manual.model';

const ZOOM_STEP = 0.25;
const ZOOM_MAX = 4;

@Component({
  selector: 'app-user-manual',
  templateUrl: './user-manual.component.html',
  styleUrls: ['./user-manual.component.scss'],
  standalone: true,
  imports: [CommonModule, SwDrawerComponent],
})
export class UserManualComponent implements OnInit, OnDestroy {

  private manual = inject(UserManualService);

  visible = false;
  activeTopic: ManualTopic = MANUAL_GROUPS[0].topics[0];
  railOpen = false;

  /** The screenshot being viewed full size, or null when none is open. */
  lightbox: ManualStep['image'] | null = null;

  zoom = 1;
  panX = 0;
  panY = 0;

  /** Where a drag started, plus the offset it started from. Null when not dragging. */
  private dragFrom: { x: number; y: number; panX: number; panY: number } | null = null;

  private readonly expanded = new Set<string>();

  @ViewChild('contentPane') private contentPane?: ElementRef<HTMLElement>;

  /**
   * Only the section the manual was opened from. Opening a Users topic lists the Users
   * groups and nothing else, so a reader on the Users screens is never shown Sites
   * topics they did not ask about.
   */
  get groups(): ManualGroup[] {
    return MANUAL_GROUPS.filter((group) => group.section === this.activeSection);
  }

  private get activeSection(): string {
    const group = this.groupOfActive();
    return group ? group.section : MANUAL_GROUPS[0].section;
  }

  /** The group holding the open topic, whether it is a parent or one of its children. */
  private groupOfActive(): ManualGroup | undefined {
    return MANUAL_GROUPS.find((group) => group.topics.some((topic) =>
      topic.id === this.activeTopic.id || !!topic.children?.some((child) => child.id === this.activeTopic.id)));
  }

  private openSub?: Subscription;

  ngOnInit(): void {
    this.openSub = this.manual.openTopic$.subscribe((topicId) => {
      const found = this.findTopic(topicId);
      if (found) {
        this.activeTopic = found;
      }
      this.railOpen = false;
      this.visible = true;
    });
  }

  ngOnDestroy(): void {
    this.openSub?.unsubscribe();
  }

  onVisibleChange(open: boolean) {
    this.visible = open;
    if (!open) {
      this.railOpen = false;
    }
  }

  selectTopic(topic: ManualTopic) {
    this.railOpen = false;
    this.activeTopic = topic;
    if (topic.children?.length) {
      this.expanded.add(topic.id);
    }
    // Otherwise a jump from halfway down one topic drops you halfway down the next.
    this.contentPane?.nativeElement.scrollTo({ top: 0 });
  }

  /** Follows a step's "See more" into the sub-topic that covers it in full. */
  goToTopic(topicId: string) {
    const found = this.findTopic(topicId);
    if (found) {
      this.selectTopic(found);
    }
  }

  /** A branch opens on click and stays open while you read anything inside it. */
  toggleBranch(topic: ManualTopic, event: MouseEvent) {
    event.stopPropagation();
    if (this.expanded.has(topic.id)) {
      this.expanded.delete(topic.id);
    } else {
      this.expanded.add(topic.id);
    }
  }

  isExpanded(topic: ManualTopic): boolean {
    return this.expanded.has(topic.id) || this.isAncestorOfActive(topic);
  }

  isAncestorOfActive(topic: ManualTopic): boolean {
    return !!topic.children?.some((child) => child.id === this.activeTopic.id);
  }

  /** The parent topic of the open one, for the breadcrumb above the title. */
  get activeParent(): ManualTopic | null {
    for (const group of MANUAL_GROUPS) {
      for (const topic of group.topics) {
        if (topic.children?.some((child) => child.id === this.activeTopic.id)) {
          return topic;
        }
      }
    }
    return null;
  }

  toggleRail() {
    this.railOpen = !this.railOpen;
  }

  openLightbox(image: ManualStep['image']) {
    this.lightbox = image ?? null;
    this.resetZoom();
  }

  closeLightbox() {
    this.lightbox = null;
    this.resetZoom();
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    this.closeLightbox();
  }

  // ---- zooming the opened screenshot ----

  zoomIn() {
    this.setZoom(this.zoom + ZOOM_STEP);
  }

  zoomOut() {
    this.setZoom(this.zoom - ZOOM_STEP);
  }

  resetZoom() {
    this.zoom = 1;
    this.panX = 0;
    this.panY = 0;
    this.dragFrom = null;
  }

  /** Wheel over the image zooms instead of scrolling the page behind it. */
  onWheel(event: WheelEvent) {
    event.preventDefault();
    this.setZoom(this.zoom + (event.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP));
  }

  /** Double-click toggles between fit and a closer look, the way image viewers do. */
  toggleZoom() {
    this.setZoom(this.zoom > 1 ? 1 : 2);
  }

  startPan(event: MouseEvent) {
    if (this.zoom <= 1) {
      return;
    }
    event.preventDefault();
    this.dragFrom = { x: event.clientX, y: event.clientY, panX: this.panX, panY: this.panY };
  }

  @HostListener('document:mousemove', ['$event'])
  onPan(event: MouseEvent) {
    if (!this.dragFrom) {
      return;
    }
    this.panX = this.dragFrom.panX + (event.clientX - this.dragFrom.x);
    this.panY = this.dragFrom.panY + (event.clientY - this.dragFrom.y);
  }

  @HostListener('document:mouseup')
  endPan() {
    this.dragFrom = null;
  }

  get zoomPercent(): number {
    return Math.round(this.zoom * 100);
  }

  get imageTransform(): string {
    return `translate(${this.panX}px, ${this.panY}px) scale(${this.zoom})`;
  }

  private setZoom(value: number) {
    this.zoom = Math.min(ZOOM_MAX, Math.max(1, Math.round(value * 100) / 100));
    if (this.zoom === 1) {
      // Back to fit: a leftover offset would leave the image sitting off-centre.
      this.panX = 0;
      this.panY = 0;
    }
  }

  get nextTopic(): ManualTopic | null {
    const flat = this.flatTopics();
    const index = flat.findIndex((topic) => topic.id === this.activeTopic.id);
    return index > -1 && index < flat.length - 1 ? flat[index + 1] : null;
  }

  get previousTopic(): ManualTopic | null {
    const flat = this.flatTopics();
    const index = flat.findIndex((topic) => topic.id === this.activeTopic.id);
    return index > 0 ? flat[index - 1] : null;
  }

  get activeGroupTitle(): string {
    const group = this.groupOfActive();
    return group ? group.title : '';
  }

  /**
   * Reading order for Prev/Next: parent, then its sub-modules, then the next parent —
   * the same order the rail shows. Current section only, so they never cross into
   * another one.
   */
  private flatTopics(): ManualTopic[] {
    const flat: ManualTopic[] = [];
    for (const group of this.groups) {
      for (const topic of group.topics) {
        flat.push(topic, ...(topic.children ?? []));
      }
    }
    return flat;
  }

  /** Searches every section: the topic being opened is what decides the section. */
  private findTopic(topicId: string | null): ManualTopic | null {
    if (!topicId) {
      return null;
    }
    for (const group of MANUAL_GROUPS) {
      for (const topic of group.topics) {
        if (topic.id === topicId) {
          return topic;
        }
        const child = topic.children?.find((item) => item.id === topicId);
        if (child) {
          return child;
        }
      }
    }
    return null;
  }

}
